import { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { Particles } from "@/components/aiot/lab/LabFx";

const HOT = new THREE.Color("#DC2626"), COOL = new THREE.Color("#1D4ED8");
const damp = (cur, to, dt, k = 4) => cur + (to - cur) * (1 - Math.exp(-k * dt));
const N = 9, START = -2.5, LEN = 5.1;
const STACK = [["red", "#EF4444", 0.13], ["amber", "#F59E0B", 0], ["green", "#22C55E", -0.13]];

const Bottles = ({ fx, jamX }) => {
  const g = useRef();
  const st = useRef({ x: 0, v: 0 });
  const seeds = useMemo(() => Array.from({ length: N }, (_, i) => i * (LEN / N)), []);
  useFrame((_, dt) => {
    if (!g.current) return;
    st.current.v = damp(st.current.v, fx.belt && !fx.jam ? (fx.slow ? 0.25 : 0.5) : 0, dt, 3);
    st.current.x += st.current.v * dt;
    const xs = seeds.map((s) => START + ((s + st.current.x) % LEN));
    const queue = xs.map((x, i) => [x, i]).filter(([x]) => x < jamX).sort((a, c) => c[0] - a[0]).map(([, i]) => i);
    g.current.children.forEach((b, i) => {
      const k = fx.jam ? queue.indexOf(i) : -1;
      if (k >= 0) b.position.x = damp(b.position.x, jamX - k * 0.13, dt, 5);
      else b.position.x = xs[i];
      b.rotation.z = damp(b.rotation.z, k === 1 ? Math.PI / 2.4 : 0, dt, 5);
    });
  });
  return (
    <group ref={g}>
      {seeds.map((s) => (
        <group key={s} position={[START + s, 0.97, 0]}>
          <mesh castShadow><cylinderGeometry args={[0.055, 0.06, 0.22, 16]} /><meshStandardMaterial color="#7DD3FC" transparent opacity={0.8} roughness={0.1} /></mesh>
          <mesh position={[0, 0.13, 0]}><cylinderGeometry args={[0.03, 0.03, 0.04, 12]} /><meshStandardMaterial color="#DC2626" /></mesh>
        </group>
      ))}
    </group>
  );
};

const Motor = ({ room, fx }) => {
  const g = useRef(), mat = useRef();
  useFrame(({ clock }, dt) => {
    if (mat.current) {
      mat.current.color.lerp(fx.mhot ? HOT : COOL, 1 - Math.exp(-2 * dt));
      mat.current.emissiveIntensity = damp(mat.current.emissiveIntensity, fx.mhot ? 0.6 : 0, dt, 2);
    }
    if (g.current) g.current.position.y = room.motor[1] + (fx.vibe && fx.belt ? Math.sin(clock.elapsedTime * 70) * 0.012 : 0);
  });
  return (
    <group>
      <group ref={g} position={room.motor}>
        <mesh rotation={[Math.PI / 2, 0, 0]} castShadow><cylinderGeometry args={[0.2, 0.2, 0.5, 24]} /><meshStandardMaterial ref={mat} color="#1D4ED8" emissive="#F97316" emissiveIntensity={0} metalness={0.3} /></mesh>
        <mesh position={[0, 0, 0.28]} rotation={[Math.PI / 2, 0, 0]}><cylinderGeometry args={[0.04, 0.04, 0.1, 12]} /><meshStandardMaterial color="#CBD5E1" metalness={0.7} /></mesh>
      </group>
      {fx.mhot && <Particles origin={[room.motor[0], room.motor[1] + 0.2, 0]} count={14} color="#F97316" rise={[0, 1.2, 0]} spread={0.4} size={0.05} opacity={0.6} life={1.8} />}
      {fx.vibe && <Particles origin={room.bearing} count={10} color="#94A3B8" rise={[0.6, 0.3, 0.6]} spread={0.25} size={0.04} opacity={0.7} life={0.9} />}
    </group>
  );
};

const Operator = ({ room, fx }) => {
  const g = useRef(), arm = useRef(), t = useRef(0), r = useRef(0);
  useFrame((_, dt) => {
    t.current = damp(t.current, fx.person ? 1 : 0, dt, 2);
    r.current = damp(r.current, fx.hand ? 1 : 0, dt, 3);
    if (g.current) { g.current.visible = t.current > 0.03; g.current.scale.setScalar(Math.min(1, t.current * 2)); }
    if (arm.current) { arm.current.rotation.x = -r.current * 1.35; arm.current.rotation.z = r.current * 0.35; }
  });
  return (
    <group ref={g} position={room.person}>
      <mesh position={[0, 0.75, 0]} castShadow><capsuleGeometry args={[0.18, 0.75, 6, 12]} /><meshStandardMaterial color="#0F766E" /></mesh>
      <mesh position={[0, 1.45, 0]} castShadow><sphereGeometry args={[0.15, 16, 16]} /><meshStandardMaterial color="#C08457" /></mesh>
      <mesh position={[0, 1.6, 0]}><sphereGeometry args={[0.16, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2]} /><meshStandardMaterial color="#FACC15" /></mesh>
      <group ref={arm} position={[0.22, 1.25, 0]}><mesh position={[0, -0.3, 0]}><capsuleGeometry args={[0.05, 0.5, 4, 8]} /><meshStandardMaterial color="#0F766E" /></mesh></group>
    </group>
  );
};

const Glow = ({ p, color, on, r = 0.07 }) => (
  <mesh position={p}><sphereGeometry args={[r, 16, 16]} /><meshStandardMaterial color={color} emissive={color} emissiveIntensity={on ? 3 : 0} transparent opacity={on ? 0.95 : 0} /></mesh>
);

const Signals = ({ room, fx }) => {
  const buzz = useRef(), lamp = useRef();
  useFrame(({ clock }, dt) => {
    if (buzz.current) buzz.current.intensity = fx.buzz ? (Math.sin(clock.elapsedTime * 12) > 0 ? 10 : 0) : 0;
    if (lamp.current) lamp.current.intensity = damp(lamp.current.intensity, fx.night ? 0 : 7, dt, 3);
  });
  const [x, y, z] = room.stack;
  const on = STACK.find(([k]) => k === fx.stack);
  return (
    <group>
      <pointLight ref={lamp} position={room.lamp} color="#F8FAFC" intensity={7} distance={12} decay={1.4} />
      {STACK.map(([k, c, dy]) => <Glow key={k} p={[x, y + dy, z + 0.02]} color={c} on={fx.stack === k} />)}
      {on && <pointLight position={[x, y + on[2], z + 0.3]} color={on[1]} intensity={5} distance={4} />}
      <pointLight ref={buzz} position={room.buzz} color="#EF4444" intensity={0} distance={8} />
      <Glow p={room.panel} color="#EF4444" on={fx.estop} r={0.09} />
      {fx.hand && <mesh position={room.curtain}><boxGeometry args={[0.9, 0.7, 0.01]} /><meshStandardMaterial color="#EF4444" emissive="#EF4444" emissiveIntensity={1.2} transparent opacity={0.35} /></mesh>}
    </group>
  );
};

export const FactoryFx = ({ room, fx }) => (
  <group>
    <Bottles fx={fx} jamX={room.jam} />
    <Motor room={room} fx={fx} />
    <Operator room={room} fx={fx} />
    <Signals room={room} fx={fx} />
  </group>
);

const BASE = { night: false, belt: false, slow: false, hand: false, estop: false, mhot: false, vibe: false, jam: false, stack: null, buzz: false, person: false, broken: null };
export const FACTORY_SCRIPT = {
  base: BASE,
  intro: { belt: true, stack: "green", person: true },
  outro: { belt: true, stack: "green", person: true },
  pre: (s) => {
    const run = s.facts.includes("running");
    return { ...BASE, belt: run, stack: run ? "green" : null, person: run || s.facts.includes("operator") };
  },
  cause: { curtain_break: { hand: true }, estop_press: { estop: true }, motor_hot: { mhot: true }, vib_high: { vibe: true }, no_count_30: (st) => ({ jam: st.belt }) },
  act: { start_line: { belt: true, stack: "green" }, stop_line: { belt: false, stack: "red", buzz: true }, warn: { stack: "amber" }, slow: { slow: true },
    shutdown: { belt: false, stack: null, buzz: false, night: true, person: false }, buzz: { buzz: true } },
};
